import React from 'react';
import withShipment from '../../../withShipment';
import { MANAGE_MACHINE, SAVE_MACHINE } from './constants';

import { IconButton, TextField } from '@material-ui/core';
import SearchableTable from '../../global/Search/SearchableTable';
import SaveIcon from '@material-ui/icons/Save';
import TopToolbar from '../../global/subcomponents/topToolbar/TopToolbar';

import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import {
  editMachine,
  listMachine,
  loadStartedSession,
} from '../../../redux/actions/settings';
import { userInfoSelector } from '../../../redux/selectors/auth';
import {
  loadStartedSessionSelector,
  machineSelector,
} from '../../../redux/selectors/settings';
import ActiveSessionLockBanner from '../../global/ActiveSessionLockBanner';
import FeedbackBar from '../../feedbackBar/FeedbackBar';
import { clearStatus } from '../../../redux/actions/global';
import './ManageMachine.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@material-ui/core';
import { Tooltip } from '@mui/material';
import WriteGuard from '../../global/ReadOnly/WriteGuard';

class ManageMachine extends SearchableTable {
  constructor(props) {
    super(props);
    this.state = {
      machines: this.props.machines || [],
      // machineIds the user explicitly removed via the row Delete button.
      // Only these are deleted server-side — never a row that is merely
      // absent from the payload (which can happen with stale/partial state).
      deletedMachineIds: [],
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
      showDeleteDialog: false,
      rowToDelete: null,
      rangeStart: '',
      rangeEnd: '',
      // Blocks a second Save while one is in flight. Two overlapping saves of a
      // grid whose rows have no machineId yet is exactly how a location ended up
      // with every machine stored twice.
      saving: false,
    };
  }

  componentDidMount() {
    this.props.listMachine(this.props.userInfo.selectedLocationId);
    this.props.loadStartedSession({
      locationId: this.props.userInfo.selectedLocationId,
      isAssignMachine: true,
    });
    this.pushMenuItems();
  }

  isLocked = () => {
    const s = this.props.startedSession;
    return !!(s && s.sessionStarted);
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    // When the active location changes, clear stale local edits and refetch.
    // Otherwise this component could keep one location's machine list in state
    // and save it against a different location's id — wiping the other location.
    if (
      prevProps.userInfo.selectedLocationId !==
      this.props.userInfo.selectedLocationId
    ) {
      this.setState({ machines: [], deletedMachineIds: [] });
      this.props.listMachine(this.props.userInfo.selectedLocationId);
      this.props.loadStartedSession({
        locationId: this.props.userInfo.selectedLocationId,
        isAssignMachine: true,
      });
      return;
    }
    if (prevProps.machines !== this.props.machines) {
      if (this.props.machines && this.props.machines.length > 0) {
        const { machines, duplicateIds } = ManageMachine.dedupeMachines(
          this.props.machines
        );
        this.setState((prevState) => ({
          machines,
          deletedMachineIds: duplicateIds.length
            ? [...new Set([...prevState.deletedMachineIds, ...duplicateIds])]
            : prevState.deletedMachineIds,
        }));
        if (duplicateIds.length) {
          this.openFeedbackBar(
            'warning',
            `${duplicateIds.length} duplicate machine ${
              duplicateIds.length === 1 ? 'entry was' : 'entries were'
            } found and removed from the list below. Press Save to apply.`
          );
        }
      }
    }
    // `saving` is included so the toolbar's Save button actually re-renders as
    // disabled for the duration of the request.
    if (
      prevState.machines !== this.state.machines ||
      prevState.saving !== this.state.saving
    ) {
      this.pushMenuItems();
    }
  }

  pushMenuItems = () => {
    if (this.props.onMenuItemsChange) {
      this.props.onMenuItemsChange(this.getContextualMenuItems());
    }
  };

  getContextualMenuItems = () => [
    {
      title: SAVE_MACHINE,
      icon: <SaveIcon />,
      onClick: this.handleSave,
      disabled: this.isSaveDisabled(),
    },
  ];

  // Single definition of "same machine" — used by both the Save guard and the
  // per-row highlight below, so a row can never be rejected on save without the
  // grid showing which rows collide (or vice versa).
  static normalizeName = (name) => (name || '').trim().toLowerCase();

  // Collapse machines the server already stores twice under the same name.
  //
  // Without this, a location whose data was duplicated by an earlier bug is
  // stuck: every row renders as "Duplicate machine" and handleSave refuses the
  // whole batch, so the user has no way to clean it up from this screen. Keeping
  // the first row per name and queueing the rest into deletedMachineIds turns
  // the next Save into the fix.
  static dedupeMachines = (machines = []) => {
    const seen = new Set();
    const kept = [];
    const duplicateIds = [];

    machines.forEach((m) => {
      const name = ManageMachine.normalizeName(m.machine);
      // Blank rows are the user's in-progress typing — never collapse those.
      if (name && seen.has(name)) {
        if (m.machineId) {
          duplicateIds.push(m.machineId);
        }
        return;
      }
      if (name) {
        seen.add(name);
      }
      kept.push(m);
    });

    return { machines: kept, duplicateIds };
  };

  handleSave = () => {
    if (this.state.saving) {
      return;
    }

    const machineNames = this.state.machines.map((m) =>
      ManageMachine.normalizeName(m.machine)
    );
    const hasDuplicates = new Set(machineNames).size !== machineNames.length;

    if (hasDuplicates) {
      this.openFeedbackBar('error', 'Duplicate machine names are not allowed.');
      return;
    }

    const callback = (severity, message) => {
      if (severity) {
        this.setState({ saving: false });
        this.openFeedbackBar('error', message || 'Failed to save machines');
      } else {
        // Deletions have been applied server-side; reset the pending list so a
        // later save doesn't re-send stale IDs.
        this.setState({ saving: false, deletedMachineIds: [] });
        this.openFeedbackBar('success', message || 'Saved');
      }
    };

    this.setState({ saving: true });
    this.props.editMachine(
      this.state.machines,
      this.props.userInfo.selectedLocationId,
      this.state.deletedMachineIds,
      callback
    );
  };

  isSaveDisabled = () => {
    return (
      this.isLocked() ||
      this.state.saving ||
      this.state.machines.some((template) => !template.machine)
    );
  };

  addTemplateRow = () => {
    this.setState((prevState) => {
      const updatedMachines = [
        ...prevState.machines,
        {
          machine: '',
        },
      ];
      return { machines: updatedMachines };
    });
  };
  removeTemplateRow = (index) => {
    this.setState((prevState) => {
      const removed = prevState.machines[index];
      const updatedMachines = prevState.machines.filter(
        (_, curIndex) => curIndex !== index
      );
      // Record the deletion of an already-saved row so the server deletes it
      // explicitly. New (unsaved) rows have no machineId — nothing to delete.
      const updatedDeleted =
        removed && removed.machineId
          ? [...prevState.deletedMachineIds, removed.machineId]
          : prevState.deletedMachineIds;
      return { machines: updatedMachines, deletedMachineIds: updatedDeleted };
    });
  };

  handleTemplateChange = (e, index) => {
    const { name, value } = e.target;
    this.setState((prevState) => {
      const updatedMachines = [...prevState.machines];
      updatedMachines[index] = {
        ...updatedMachines[index],
        [name]: value,
      };

      return { machines: updatedMachines };
    });
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  handleRangeChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  generateMachines = () => {
    const { rangeStart, rangeEnd, machines } = this.state;
    const start = parseInt(rangeStart, 10);
    const end = parseInt(rangeEnd, 10);

    if (isNaN(start) || isNaN(end) || start > end) {
      this.openFeedbackBar('error', 'Invalid range');
      return;
    }

    const existingNames = new Set(
      machines.map((m) => ManageMachine.normalizeName(m.machine))
    );
    const newMachines = [];

    for (let i = start; i <= end; i++) {
      const machineName = `${i}`;
      if (!existingNames.has(ManageMachine.normalizeName(machineName))) {
        newMachines.push({ machine: machineName });
        // Add immediately so the same name can't be generated twice
        existingNames.add(ManageMachine.normalizeName(machineName));
      }
    }

    if (newMachines.length === 0) {
      this.openFeedbackBar(
        'error',
        'All machines in the selected range already exist.'
      );
      return;
    }

    const updatedMachines = [...machines, ...newMachines];

    // Sort by number ascending
    updatedMachines.sort(
      (a, b) => parseInt(a.machine, 10) - parseInt(b.machine, 10)
    );

    this.setState({
      machines: updatedMachines,
      rangeStart: '',
      rangeEnd: '',
    });
  };

  render() {
    const { machines } = this.state;

    // Count each name once instead of rescanning the whole list per row —
    // these grids routinely hold 80+ machines.
    const nameCounts = machines.reduce((counts, m) => {
      const name = ManageMachine.normalizeName(m.machine);
      if (name) counts[name] = (counts[name] || 0) + 1;
      return counts;
    }, {});
    const isDuplicate = (m) =>
      nameCounts[ManageMachine.normalizeName(m.machine)] > 1;

    return (
      <React.Fragment>
        {!this.props.embedded && (
          <TopToolbar
            pageName={MANAGE_MACHINE}
            menuItems={this.getContextualMenuItems()}
          />
        )}

        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />

        {this.isLocked() && (
          <ActiveSessionLockBanner
            sessionId={this.props.startedSession?.sessionId}
          />
        )}

        <Dialog
          open={this.state.showDeleteDialog}
          onClose={() => this.setState({ showDeleteDialog: false })}
        >
          <DialogTitle>{'Confirm Delete'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this machine?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => this.setState({ showDeleteDialog: false })}
              style={{
                width: '100px',
                marginRight: '16px',
                marginBottom: '16px',
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                this.removeTemplateRow(this.state.rowToDelete);
                this.setState({
                  showDeleteDialog: false,
                  rowToDelete: null,
                });
              }}
              color="primary"
              style={{
                width: '100px',
                marginRight: '16px',
                marginBottom: '16px',
              }}
            >
              {' '}
              Delete
            </Button>
          </DialogActions>
        </Dialog>

          {/* The whole editable surface — the Generate Machines range AND
              the grid below it. Greyed and made inert at a VIEW-ONLY
              location rather than hidden, because the point of this screen
              is seeing the configuration; only changing it is off. The
              Save button is already dropped by the toolbar filter.
              See constants/readOnlyAccess.js. */}
          <WriteGuard mode="disable" noTooltip>
        <div className="manage-machine-range">
          <TextField
            variant="outlined"
            label="Start Range"
            name="rangeStart"
            InputLabelProps={{ shrink: true }}
            value={this.state.rangeStart}
            onChange={this.handleRangeChange}
            type="number"
            className="manage-machine-input"
          />
          <TextField
            variant="outlined"
            label="End Range"
            name="rangeEnd"
            InputLabelProps={{ shrink: true }}
            value={this.state.rangeEnd}
            onChange={this.handleRangeChange}
            type="number"
            className="manage-machine-input"
          />
          <Button
            className="manage-machine-button"
            variant="contained"
            color="primary"
            onClick={this.generateMachines}
            disabled={!this.state.rangeStart || !this.state.rangeEnd}
          >
            Generate Machines
          </Button>
        </div>

        <div className="manage-machine-list">
          <div>
            {machines.map((template, index) => (
              <div className="row-manage-card" key={index}>
                <div className="remove-icon-wrapper">
                  <IconButton
                    onClick={() =>
                      this.setState({
                        showDeleteDialog: true,
                        rowToDelete: index,
                      })
                    }
                  >
                    <RemoveCircleIcon fontSize="medium" color="error" />
                  </IconButton>
                </div>

                <TextField
                  variant="outlined"
                  name="machine"
                  label="Machine"
                  InputLabelProps={{ shrink: true }}
                  value={template.machine || ''}
                  onChange={(e) =>
                    this.handleTemplateChange(
                      {
                        target: {
                          name: e.target.name,
                          value: e.target.value,
                        },
                      },
                      index
                    )
                  }
                  required
                  error={!template.machine || isDuplicate(template)}
                  helperText={
                    !template.machine
                      ? ''
                      : isDuplicate(template)
                      ? 'Duplicate machine'
                      : ''
                  }
                />
              </div>
            ))}
            <div className="add-machine-btn-wrapper">
              <Tooltip title={'Add machine'}>
                <IconButton onClick={this.addTemplateRow}>
                  <AddCircleIcon fontSize="medium" color="secondary" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
          </WriteGuard>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  machines: machineSelector(state),
  startedSession: loadStartedSessionSelector(state),
});

const actionCreators = {
  listMachine,
  editMachine,
  clearStatus,
  loadStartedSession,
};
export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  ManageMachine
);
